/**
 * The page for billing details.
 *
 * Copyright (c) Ircam Amplify 2025-2026
 */

import { API, fetchConsumptionDataBatch, fetchInvoice } from '@/api'
import FormReturnHandler from '@/components/FormReturnHandler'
import { handleUpdatePayment } from '@/components/MyAccount'
import TabContainer from '@/components/TabContainer'
import './billing.css'

import { logger } from '@/config/logger'
import { getUserState, useSelector } from '@/store'

import {
  AccountUtil,
  CompanyConsumption,
  InvoiceInfo,
  InvoicePdfLink,
  Kpi,
  LogTag,
  TierType,
} from '@amplify3/duplo-back-end'
import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import LinearProgress from '@mui/material/LinearProgress'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useEffect, useState } from 'react'
import AccountManagement from './AccountManagement'
import BillingHistory from './BillingHistory'
import { getInvoice, PlanInvoice } from './Invoice'
import PlanOverview from './PlanOverview'
import UpcomingBill from './UpcomingBill'
import UsageTab from './UsageTab'

export const Billing = () => {
  const { companyConsumption, companyKpi, userInfo, invoiceInfo } =
    useSelector(getUserState)

  const [isLoadingData, setLoadingData] = useState(true)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [planInvoice, setPlanInvoice] = useState<PlanInvoice | undefined>(
    undefined
  )
  const [invoicePdfList, setInvoicePdfList] = useState<InvoicePdfLink[]>([])

  const confirmCancellation = async () => {
    try {
      setIsCancelling(true)
      setIsProcessing(true)
      await API(await fetchAuthSession()).delete('/subscription')
      setIsCancelling(false)
      setIsProcessing(false)
    } catch (e: any) {
      setErrorMessage(
        'Error cancelling subscription : ' + e.response.data.error
      )
      setIsCancelling(false)
      setIsProcessing(false)
    }
  }

  /**
   * Get the list of invoices and the consumption data
   * from the responses of the backend.
   */
  function getPlanInvoice(
    consumption: CompanyConsumption,
    kpiList: Kpi[],
    invoice: InvoiceInfo
  ) {
    if (!invoiceInfo) {
      return
    }

    const plan = getInvoice(consumption, kpiList, invoice)
    setPlanInvoice(plan)
    logger.debug(LogTag.BillingPage, 'PlanInvoice:', plan)

    const pdfLinkList = InvoicePdfLink.fromInvoiceList(invoiceInfo.value)
    setInvoicePdfList((prev) => {
      // Filter the existing invoices to handle concurrent calls to this
      // function and the race conditions.
      const existingIds = new Set((prev ?? []).map((i) => i.id))
      const newInvoices = pdfLinkList.filter(
        (i: InvoicePdfLink) => !existingIds.has(i.id)
      )
      return [...(prev ?? []), ...newInvoices]
    })
  }

  useEffect(() => {
    fetchConsumptionDataBatch()
      .catch((err) => logger.error('Failed to load billing data:', err))
      .finally(() => setLoadingData(false))
  }, [])

  // Request data from backend when consumption and KPI are updated
  useEffect(() => {
    if (companyConsumption && companyKpi && invoiceInfo) {
      getPlanInvoice(
        companyConsumption.value,
        companyKpi.value,
        invoiceInfo.value
      )
    }
  }, [companyConsumption, companyKpi, invoiceInfo])

  const TabPlanDetails = () => {
    const updatePaymentHandler =
      TierType.PayAsYouGo === planInvoice?.tier ||
      TierType.Subscription === planInvoice?.tier
        ? () => handleUpdatePayment(userInfo?.value)
        : undefined

    return (
      <div className="bill-plan-details">
        <div className="column column-left">
          <PlanOverview
            className="billing-component-container"
            invoice={planInvoice}
          />
          <BillingHistory
            className="billing-component-container"
            invoicePdfList={invoicePdfList}
            getInvoices={fetchInvoice}
            hasMoreInvoicePage={invoiceInfo?.value?.has_more ?? false}
            lastInvoiceId={invoiceInfo?.value?.last_key ?? ''}
          />
        </div>
        {TierType.TopUpCreditAimd != planInvoice?.tier && (
          <div className="column column-right">
            <UpcomingBill
              className="billing-component-container"
              invoice={planInvoice}
            />
            <AccountManagement
              className="billing-component-container"
              cancelPlan={setIsCancelling}
              updatePayment={updatePaymentHandler}
              cancelAccountPlan={async () => setIsCancelling(true)}
            />
          </div>
        )}
      </div>
    )
  }

  const TabUsage = ({
    className,
    title,
  }: {
    className?: string
    title?: string
  }) => {
    return (
      <UsageTab
        className={className}
        title={title}
        invoice={planInvoice}
        kpiList={companyKpi?.value ?? []}
      />
    )
  }

  const LoadingProgress = () => {
    return (
      <div className="billing-loading-container">
        <div className="linear-progress">
          <LinearProgress
            sx={{
              backgroundColor: '#ffeae6',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#f67d69',
              },
            }}
          />{' '}
        </div>
        Loading...
      </div>
    )
  }

  return (
    <div className="billing-page">
      <Dialog
        open={isCancelling}
        onClose={() => {
          setIsCancelling(false)
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="mx-auto w-[50rem] rounded-lg p-20 bg-primary-50">
            <DialogTitle className="text-center text-[22px] mb-4">
              Cancel subscription
            </DialogTitle>
            <Description className="py-4 text-center text-[18px] mb-4">
              Are you sure you wish to cancel your subscription?
            </Description>

            <div className="w-1/2 mx-auto">
              <div className="grid grid-cols-2 gap-2">
                <div className="w-full items-center">
                  <button
                    type="submit"
                    className="block w-full button button-large button-light py-2 px-4 my-2"
                    onClick={() => setIsCancelling(false)}
                  >
                    <div className="button button-label">No</div>
                  </button>
                </div>
                <div className="w-full justify-center">
                  <button
                    type="submit"
                    className="block w-full button button-large button-dark py-2 px-4 my-2"
                    onClick={confirmCancellation}
                    disabled={isProcessing}
                  >
                    <div className="button button-label">Yes</div>
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {errorMessage && (
        <FormReturnHandler
          open={!!errorMessage}
          success={false}
          message={errorMessage}
          onClose={() => setErrorMessage('')}
        />
      )}

      <div className="billing-container">
        {isLoadingData ? (
          // Progress bar during the loading
          <LoadingProgress />
        ) : AccountUtil.isTierEnterprise(
            companyConsumption?.value?.subscription_tier as TierType
          ) ? (
          // Enterprise tier: show only the usage data
          <TabUsage />
        ) : (
          // Other types of the tier: show tabs for usage and subscription plan
          <TabContainer tabList={['Usage', 'Plan Details']}>
            <TabUsage
              className="billing-component-container"
              title="Usage Table"
            />
            <TabPlanDetails />
          </TabContainer>
        )}
      </div>
    </div>
  )
}
